import Phaser from "phaser";
import type { PlatformAdapter } from "../../adapters/PlatformAdapter";

export interface GameOverPayload {
  victory: boolean;
  wavesCleared: number;
  totalWaves: number;
  goldEarned: number;
  livesLeft: number;
  durationMs: number;
}

/**
 * Shown after the run ends. Stats + retry + share. Designed to be the
 * frame the user is most likely to screenshot for a TikTok clip.
 */
export class GameOverScene extends Phaser.Scene {
  private payload!: GameOverPayload;

  constructor() {
    super({ key: "GameOverScene" });
  }

  init(payload: GameOverPayload): void {
    this.payload = payload;
  }

  create(): void {
    const adapter = this.registry.get("adapter") as PlatformAdapter;
    const { width, height } = this.scale;
    const v = this.payload.victory;

    // Backdrop
    const dim = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.85);
    dim.setInteractive();

    // Big title
    const title = this.add
      .text(width / 2, height * 0.2, v ? "VICTORY!" : "DEFEAT", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "56px",
        fontStyle: "bold",
        color: v ? "#fff066" : "#ff4d4d",
      })
      .setOrigin(0.5)
      .setShadow(0, 6, "#000", 10, false, true);
    title.setScale(0.3);
    this.tweens.add({ targets: title, scale: 1, duration: 380, ease: "Back.easeOut" });

    // Subtitle
    this.add
      .text(width / 2, height * 0.28, v ? "all 10 waves cleared" : "your base fell", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
        color: "#aaa",
      })
      .setOrigin(0.5);

    // Stats panel
    const panelY = height * 0.45;
    const panel = this.add
      .rectangle(width / 2, panelY, width - 60, 180, 0x141a35)
      .setStrokeStyle(2, v ? 0xfff066 : 0xff4d4d, 0.7);
    void panel;

    const stats: Array<[string, string]> = [
      ["Waves cleared", `${this.payload.wavesCleared} / ${this.payload.totalWaves}`],
      ["Lives remaining", `${this.payload.livesLeft}`],
      ["Gold earned", `$${this.payload.goldEarned}`],
      ["Run time", this.formatDuration(this.payload.durationMs)],
    ];
    stats.forEach(([k, val], i) => {
      const y = panelY - 60 + i * 36;
      this.add
        .text(width / 2 - 100, y, k, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "13px",
          color: "#88a",
        })
        .setOrigin(0, 0.5);
      this.add
        .text(width / 2 + 100, y, val, {
          fontFamily: "system-ui, sans-serif",
          fontSize: "15px",
          fontStyle: "bold",
          color: "#fff",
        })
        .setOrigin(1, 0.5);
    });

    // Buttons
    const retry = this.add
      .text(width / 2, height * 0.72, "↻  RETRY", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "26px",
        fontStyle: "bold",
        color: "#0b1020",
        backgroundColor: "#7be36b",
        padding: { x: 24, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    retry.on("pointerdown", () => {
      adapter.haptic("medium");
      this.scene.start("GameScene");
    });

    const share = this.add
      .text(width / 2, height * 0.81, "📣  SHARE", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "16px",
        color: "#0b1020",
        backgroundColor: "#4cc9f0",
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    share.on("pointerdown", () => {
      adapter.haptic("light");
      const msg = v
        ? `🏆 Beat all 10 waves of Element TD with ${this.payload.livesLeft} lives left!`
        : `💀 Got crushed on wave ${this.payload.wavesCleared} of Element TD. Try again!`;
      adapter.share(msg);
    });

    const menu = this.add
      .text(width / 2, height * 0.88, "← menu", {
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px",
        color: "#aaa",
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    menu.on("pointerdown", () => this.scene.start("MenuScene"));

    // Background flash on entry for drama
    this.cameras.main.flash(280, v ? 250 : 200, v ? 240 : 50, v ? 100 : 50, true);
    if (v) adapter.haptic("success"); else adapter.haptic("error");
  }

  private formatDuration(ms: number): string {
    const sec = Math.floor(ms / 1000);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }
}
